import { useMutation } from '@apollo/client';
import { track } from '@bnnvara/analytics/src/track';
import { FlashMessage } from '@bnnvara/flash-message';
/* eslint-disable react/jsx-no-target-blank */
import PropTypes from 'prop-types';
import React, { useContext, useEffect, useRef, useState } from 'react';
import { useTheme } from 'styled-components';

import Bookmark from '../components/Bookmark';
import { CommentButton } from '../components/CommentButton/CommentButton';
import { Cta } from '../components/Cta/Cta';
import { Icon } from '../components/Icon';
import RecommendationSlider from '../components/RecommendationSlider';
import { Share } from '../components/Share/Share';
import { onPlayClick } from '../components/VideoPlayer/onPlayClick';
import NPOEventContext from '../context/NPOVideoPlayerContext';
import { PUBLICATION_VIEWED } from '../graphql/mutations/PublicationViewed.gql';
import createUrl from '../util/createUrl';
import * as S from './styled';
import { AUDIO_TYPE, VIDEO_TYPE } from './types';

const BRANDS_WITH_COMMENTS = [
  'kassa',
  'over mijn lijk',
  'oml',
  'vroege vogels tv',
  'vroege vogels',
];

export const NPOVideoPlayer = ({
  cta = undefined,
  embedId,
  mediaId,
  isBroadcastEnabled,
  programTitle,
  title,
  userId,
  type,
  slug,
}) => {
  const [shareIsActive, setShareIsActive] = useState(false);
  const [status, setStatus] = useState('');
  const [hasStarted, setHasStarted] = useState(false);
  const [textBoxIsVisible, setTextBoxIsVisible] = useState(false);
  const [ctaIsVisible, setCtaIsVisible] = useState(false);

  const copyTextBox = useRef();
  const theme = useTheme();
  const { emitter } = useContext(NPOEventContext);
  const isCtaAllowed = ctaIsVisible && cta?.title;
  const [publicationViewed] = useMutation(PUBLICATION_VIEWED);

  useEffect(() => {
    const offs = [];
    offs.push(
      emitter.on('NPO_CANPLAY', (eventName, payload) => {
        setHasStarted(true);
        onPlayClick({
          publicationViewed,
          userId,
          mediaId,
        });
        setStatus('active');

        if (!(payload.metadata.duration && payload.duration)) {
          setCtaIsVisible(true);
        }
      })
    );

    offs.push(
      emitter.on('NPO_USER_ACTIVE', () => {
        if (hasStarted) {
          setStatus('active');
        }
      })
    );

    offs.push(
      emitter.on('NPO_PAUSE', () => {
        setCtaIsVisible(true);
      })
    );

    offs.push(
      emitter.on('NPO_RESUME', () => {
        setCtaIsVisible(false);
      })
    );

    offs.push(
      emitter.on('NPO_USER_INACTIVE', () => {
        setStatus('inactive');
      })
    );

    offs.push(
      emitter.on('NPO_TIMEUPDATE', (eventName, payload) => {
        if (
          payload.position >=
          (payload.metadata.duration || payload.duration) * 0.75
        ) {
          setCtaIsVisible(true);
        }
      })
    );

    return () => {
      offs.forEach(emitter.off);
    };
  }, [hasStarted, emitter, mediaId, publicationViewed, userId]);

  const toggleShareFn = ({ isCopiedToClipboard = false, isLink }) => {
    setShareIsActive(!shareIsActive);

    if (isCopiedToClipboard) {
      copyTextBox.current = isLink ? 'Link gekopiëerd' : 'Embed gekopiëerd';
      setTextBoxIsVisible(true);
    }

    // Ga track event
    track(
      'video-player-share',
      'clickToToggleShareModal',
      shareIsActive ? 'closes-share' : 'opens-share'
    );
  };

  const slugMedia =
    type !== 'audio' && !!programTitle
      ? createUrl(programTitle, type, mediaId)
      : null;

  const isCommentable =
    BRANDS_WITH_COMMENTS.includes(programTitle?.toLowerCase()) &&
    !window.location.hostname.includes('bnnvara');

  const isShareable = slug && slug.length > 0;

  const canPlay = isBroadcastEnabled || type !== 'npo';

  if (canPlay) {
    return (
      <>
        {type !== 'audio' && (
          <S.TopIcons className="js-top-icons" status={status}>
            {isShareable && (
              <S.ShareToggleButton type="button" onClick={toggleShareFn}>
                <Icon icon="share" size="25" color={theme.base.white} />
                {textBoxIsVisible && (
                  <FlashMessage
                    message={copyTextBox.current}
                    color={theme.base.white}
                  />
                )}
              </S.ShareToggleButton>
            )}
            {userId !== 'null' && (
              <Bookmark mediaId={mediaId} userId={userId} />
            )}
            {isCommentable && <CommentButton detailPageUrl={slugMedia} />}
          </S.TopIcons>
        )}

        {isCtaAllowed && (
          <Cta
            as="a"
            href={cta?.url || null}
            target="_blank"
            rel="noopener"
            onClick={() => track('video-player-cta', 'click', cta?.url)}
          >
            {cta.title}
          </Cta>
        )}
        {type !== 'audio' && <RecommendationSlider id={mediaId} />}
        <S.Iframe
          src={`https://start-player.npo.nl/embed/${embedId}`}
          title={title}
          width="100%"
          height="100%"
          frameBorder="0"
          scrolling="no"
          allowFullScreen
          webkitallowfullscreen="true"
          mozallowfullscreen="true"
          allow="encrypted-media"
          type={type}
        />
        {shareIsActive && (
          <Share
            toggleShareFn={toggleShareFn}
            mediaId={mediaId}
            detailPageUrl={slugMedia}
            type={type}
          />
        )}
      </>
    );
  }

  // if there's an audio fragment that be be played by us
  if (type === 'audio') {
    return (
      <S.CantBeFoundAudio>
        Sorry, radio-uitzending niet gevonden
      </S.CantBeFoundAudio>
    );
  }

  return (
    <S.NPOLink>
      Bekijk deze uitzending op &nbsp;
      <a
        href="https://www.npostart.nl"
        target="_blank"
        rel="noopener"
        onClick={() => track('videoplayer-npo-pip', 'click', 'NPO Start')}
      >
        NPO Start
      </a>
    </S.NPOLink>
  );
};

NPOVideoPlayer.propTypes = {
  type: PropTypes.oneOf([VIDEO_TYPE, AUDIO_TYPE, 'npo']).isRequired,
  title: PropTypes.string.isRequired,
  mediaId: PropTypes.string.isRequired,
  embedId: PropTypes.string.isRequired,
  userId: PropTypes.string,
  isBroadcastEnabled: PropTypes.bool.isRequired,
  programTitle: PropTypes.string,
  slug: PropTypes.string,
  cta: PropTypes.shape({
    title: PropTypes.string,
    url: PropTypes.string,
  }),
};
